import { ImageVariant, ImageVariantSize } from '-/Custom/Util.types';
import { Image as ExpoImage, type ImageProps, useImage } from 'expo-image';
import type { ComponentProps } from 'react';
import { PixelRatio } from 'react-native';
import {
  StyleSheet,
  type UnistylesVariants,
  withUnistyles,
} from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import getCloudflareMediaUrl from '@/utils/getCloudflareMediaUrl';
import getOffsetColor from '@/utils/getOffsetColor';
import Box from '../UI/Box';
import CustomIcon from '../Unistyles/Icon';
import CustomPressable from '../Unistyles/Pressable';

export type AvatarSizeToken = keyof typeof avatarConfig;

type AvatarProps = Omit<ImageProps, 'source' | 'size' | 'style'> & {
  backgroundColor?: string | null;
  color?: string | null;
  name?: string;
  id?: string | null;
  size?: AvatarSizeToken;
  localUrl?: string | null;
  placeholderText?: string | null;
  ring?: boolean;
  ringColor?: string | null;
  ringWidth?: number;
};

type UnistylesProps = UnistylesVariants<typeof style> & AvatarProps;
const UnistylesImage = withUnistyles(ExpoImage);

export const Icon = (props: ComponentProps<typeof CustomIcon>) => {
  return (
    <Box center style={style.iconContainer}>
      <CustomIcon color={props.color} {...props} />
    </Box>
  );
};

export const Pressable = (props: ComponentProps<typeof CustomPressable>) => {
  return <CustomPressable style={style.pressable} {...props} />;
};

function getAvatarText(text?: string | null) {
  if (text) {
    const words = text.split(' ');
    const firstLetter = words[0].charAt(0).toUpperCase();
    const secondLetter = words[1]?.charAt(0).toUpperCase() || '';
    return `${firstLetter}${secondLetter}`;
  }
  return '';
}

const avatarConfig = {
  xs: {
    width: 24,
    height: 24,
    imageVariant: ImageVariantSize.Small,
  },
  sm: {
    width: 32,
    height: 32,
    imageVariant: ImageVariantSize.Medium,
  },
  md: {
    width: 40,
    height: 40,
    imageVariant: ImageVariantSize.Medium,
  },
  lg: {
    width: 48,
    height: 48,
    imageVariant: ImageVariantSize.Large,
  },
  xl: {
    width: 64,
    height: 64,
    imageVariant: ImageVariantSize.Large,
  },
  '2xl': {
    width: 96,
    height: 96,
    imageVariant: ImageVariantSize.Large,
  },
  '3xl': {
    width: 128,
    height: 128,
    imageVariant: ImageVariantSize.Large,
  },
};

const avatarRingConfig: Record<
  AvatarSizeToken,
  { borderWidth: number; inset: number }
> = {
  xs: {
    borderWidth: 1.25,
    inset: 2,
  },
  sm: {
    borderWidth: 1.25,
    inset: 2,
  },
  md: {
    borderWidth: 1.5,
    inset: 3,
  },
  lg: {
    borderWidth: 1.5,
    inset: 3,
  },
  xl: {
    borderWidth: 2,
    inset: 4,
  },
  '2xl': {
    borderWidth: 2,
    inset: 5,
  },
  '3xl': {
    borderWidth: 2,
    inset: 6,
  },
};

export const Container = (
  props: AvatarProps & UnistylesProps & ComponentProps<typeof Box>,
) => {
  const {
    color,
    id,
    localUrl,
    name,
    placeholderText,
    ring,
    ringColor,
    size,
    style: extraStyles,
    ...boxProps
  } = props;
  const mainColor = color || '#000';

  style.useVariants({
    size,
  });

  return (
    <Box {...boxProps} style={[style.container(mainColor), extraStyles]} />
  );
};

export const Image = (props: AvatarProps) => {
  const { id, localUrl, size = 'sm' } = props;
  const imageVariant = avatarConfig[size].imageVariant;
  const scale = Math.min(Math.floor(PixelRatio.get()), 3);
  const variant = getImageVariant(imageVariant, scale);

  const url =
    localUrl ??
    (id
      ? getCloudflareMediaUrl({
          id,
          variant,
        })
      : null);

  const image = useImage(url ?? localUrl ?? '', {
    onError: () => {},
  });

  return (
    <UnistylesImage
      transition={250}
      style={style.image}
      contentFit="cover"
      recyclingKey={localUrl || id}
      key={localUrl || id}
      source={image}
    />
  );
};

function getImageVariant(size: ImageVariantSize, scale: number): ImageVariant {
  switch (size) {
    case ImageVariantSize.Large:
      return scale === 1
        ? ImageVariant.Large1x
        : scale === 2
          ? ImageVariant.Large2x
          : ImageVariant.Large3x;
    case ImageVariantSize.Medium:
      return scale === 1
        ? ImageVariant.Medium1x
        : scale === 2
          ? ImageVariant.Medium2x
          : ImageVariant.Medium3x;
    case ImageVariantSize.Small:
      return scale === 1
        ? ImageVariant.Small1x
        : scale === 2
          ? ImageVariant.Small2x
          : ImageVariant.Small3x;
  }
}

function Avatar(props: AvatarProps & ComponentProps<typeof Box>) {
  const {
    backgroundColor,
    children,
    color,
    name,
    id,
    size = 'sm',
    localUrl,
    placeholderText,
    ring = true,
    ringColor,
    ringWidth,
    style: extraStyles,
    testID,
    ...boxProps
  } = props;

  style.useVariants({
    size,
  });

  const mainColor = color || '#000';
  const bodyColor = backgroundColor ?? mainColor;
  const offsetColor = getOffsetColor(mainColor, false);

  const defaultContent = (
    <>
      {placeholderText ? (
        <Text
          allowFontScaling={false}
          bold
          color={offsetColor}
          style={style.text}
        >
          {placeholderText}
        </Text>
      ) : !localUrl && !id && name ? (
        <Text
          allowFontScaling={false}
          bold
          color={offsetColor}
          style={style.text}
        >
          {getAvatarText(name)}
        </Text>
      ) : null}
      {(localUrl || id) && !placeholderText ? (
        <Image id={id} localUrl={localUrl} size={size} />
      ) : null}
    </>
  );
  const content = children ?? defaultContent;

  const avatar = (
    <Container
      color={bodyColor}
      size={size}
      {...boxProps}
      testID={testID}
      style={extraStyles}
    >
      {content}
    </Container>
  );

  if (ring) {
    const ringConfig = avatarRingConfig[size];

    return (
      <Box
        {...boxProps}
        center
        style={[
          style.ring(
            ringColor ?? mainColor,
            ringWidth ?? ringConfig.borderWidth,
          ),
          backgroundColor === undefined
            ? undefined
            : style.ringBackground(bodyColor),
          extraStyles,
        ]}
        testID={testID}
      >
        <Box center style={style.ringBody(bodyColor, ringConfig.inset)}>
          {content}
        </Box>
      </Box>
    );
  }

  return avatar;
}

const style = StyleSheet.create((theme) => ({
  container: (backgroundColor: string) => ({
    backgroundColor,
    borderRadius: theme.borderRadius.full,
    borderColor: 'transparent',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    variants: {
      size: avatarConfig,
    },
  }),
  image: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    borderRadius: theme.borderRadius.full,
  },
  iconContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressable: {
    borderRadius: theme.borderRadius.full,
  },
  ring: (borderColor: string, borderWidth: number) => ({
    backgroundColor: theme.colors.surface,
    borderColor,
    borderRadius: theme.borderRadius.full,
    borderWidth,
    overflow: 'hidden',
    position: 'relative',
    variants: {
      size: {
        xs: {
          ...avatarConfig.xs,
        },
        sm: {
          ...avatarConfig.sm,
        },
        md: {
          ...avatarConfig.md,
        },
        lg: {
          ...avatarConfig.lg,
        },
        xl: {
          ...avatarConfig.xl,
        },
        '2xl': {
          ...avatarConfig['2xl'],
        },
        '3xl': {
          ...avatarConfig['3xl'],
        },
        default: {},
      },
    },
  }),
  ringBackground: (backgroundColor: string) => ({
    backgroundColor,
  }),
  ringBody: (backgroundColor: string, inset: number) => ({
    alignItems: 'center',
    backgroundColor,
    bottom: inset,
    borderRadius: theme.borderRadius.full,
    justifyContent: 'center',
    left: inset,
    overflow: 'hidden',
    position: 'absolute',
    right: inset,
    top: inset,
  }),
  text: {
    variants: {
      size: {
        xs: {
          fontSize: theme.fontSize.xs,
        },
        sm: {
          fontSize: theme.fontSize.sm,
        },
        md: {
          fontSize: theme.fontSize.md,
        },
        lg: {
          fontSize: theme.fontSize.lg,
        },
        xl: {
          fontSize: theme.fontSize['2xl'],
        },
        '2xl': {
          fontSize: theme.fontSize['3xl'],
        },
        '3xl': {
          fontSize: theme.fontSize['4xl'],
        },
        default: {
          fontSize: theme.fontSize.sm,
        },
      },
    },
  },
}));

export default Avatar;
